import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { RootRedirect } from "./components/RootRedirect.component";
import { authRoutes } from "./features/auth/auth.routes";
import DashboardPage from "./features/dashboard/pages/dashboard.page";
import PodWorkspacePage from "./features/podWorkspace/pages/podWorkspace.page";
import PlatformManagerDashboardPage from "./features/platformManager/pages/platformManagerDashboard.page";
import PlatformManagerUsersPage from "./features/platformManager/pages/platformManagerUsers.page";
import PlatformManagerPipelinePage from "./features/platformManager/pages/platformManagerPipeline.page";
import FeedbackManagementPage from "./features/platformManager/pages/feedbackManagement.page";
import SubscriptionManagementPage from "./features/platformManager/pages/subscriptionManagement.page";
import PricingPage from "./features/pricing/pricing.page";
import PricingSuccessPage from "./features/pricing/pages/pricingSuccess.page";
import SettingsPage from "./features/settings/pages/settings.page";
import {
	RequireAuth,
	RequirePlatformManager,
} from "./components/authWrappers.component";
import { AuthLayout } from "./layouts/authLayout";
import { MainLayout } from "./layouts/mainLayout";

// Lazy load SharedChapterWrapper to prevent it from breaking the app if there are errors
const SharedChapterWrapper = lazy(
	() => import("./features/podcast/pages/sharedPodcastChapterWrapper.page"),
);

// Lazy load SharedPodcastWrapper
const SharedPodcastWrapper = lazy(
	() => import("./features/podcast/pages/sharedPodcastWrapper.page"),
);

/**
 * This is our main routes configuration.
 * It uses a layout-based approach where different parts of the app have different layouts:
 *
 * - AuthLayout: For unauthenticated pages (login, register) - no sidebar, no player
 * - MainLayout: For authenticated pages (dashboard, etc.) - includes sidebar and player
 *
 * Route structure:
 * - / - Root redirect (redirects to dashboard if authenticated, login if not)
 * - /auth/* - Authentication routes using AuthLayout
 * - /dashboard - Main app routes using MainLayout
 *
 * This approach is cleaner than conditionally hiding components and follows
 * React Router best practices for nested layouts.
 */
export const routes: RouteObject[] = [
	{
		path: "/",
		element: <RootRedirect />,
	},
	{
		path: "/auth",
		element: <AuthLayout />,
		children: authRoutes,
	},
	{
		path: "/shared/chapter/:shareToken",
		element: <SharedChapterWrapper />,
	},
	{
		path: "/shared/podcast/:shareToken",
		element: <SharedPodcastWrapper />,
	},
	{
		path: "/",
		element: <MainLayout />,
		children: [
			{
				path: "/dashboard",
				element: (
					<RequireAuth>
						<DashboardPage />
					</RequireAuth>
				),
			},
			{
				path: "/podspace",
				element: (
					<RequireAuth>
						<PodWorkspacePage />
					</RequireAuth>
				),
			},
			{
				path: "/platform-manager",
				element: (
					<RequirePlatformManager>
						<PlatformManagerDashboardPage />
					</RequirePlatformManager>
				),
			},
			{
				path: "/platform-manager/users",
				element: (
					<RequirePlatformManager>
						<PlatformManagerUsersPage />
					</RequirePlatformManager>
				),
			},
			{
				path: "/platform-manager/pipeline",
				element: (
					<RequirePlatformManager>
						<PlatformManagerPipelinePage />
					</RequirePlatformManager>
				),
			},
			{
				path: "/platform-manager/feedback",
				element: (
					<RequirePlatformManager>
						<FeedbackManagementPage />
					</RequirePlatformManager>
				),
			},
			{
				path: "/platform-manager/subscriptions",
				element: (
					<RequirePlatformManager>
						<SubscriptionManagementPage />
					</RequirePlatformManager>
				),
			},
			{
				path: "/my-podspace/:workspaceId",
				element: (
					<RequireAuth>
						<PodWorkspacePage />
					</RequireAuth>
				),
			},
			{
				path: "/pricing",
				element: (
					<RequireAuth>
						<PricingPage />
					</RequireAuth>
				),
			},
			{
				path: "/settings",
				element: (
					<RequireAuth>
						<SettingsPage />
					</RequireAuth>
				),
			},
			{
				path: "/pricing/success",
				element: (
					<RequireAuth>
						<PricingSuccessPage />
					</RequireAuth>
				),
			},
		],
	},
];
